import { formatDateISO } from '@elka/utils';
import { ACTIVITY_TYPE_LABELS, CALL_CONNECTION_LABELS, CALL_RESULT_LABELS } from '@elka/types';
import type { ActivityInput } from '@elka/types';
import type { NewTicketInput } from '@elka/firebase';

/**
 * Tomorrow in YYYY-MM-DD. The repo has `getTodayISO` in @elka/utils but no
 * tomorrow helper; centralize it here so we don't sprinkle ad-hoc Date math.
 */
export function getTomorrowISO(): string {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  return formatDateISO(d);
}

type TitleInput = Pick<
  ActivityInput,
  'type' | 'callConnection' | 'callResult' | 'contactName' | 'companyName'
>;

/**
 * Build a non-empty Hebrew title for an activity when the user didn't type one.
 * For calls: "שיחה עם <name> · <connection>[ / <result>]" or "שיחה · <connection>".
 * Always returns a non-empty string (Activity.title is required), falling back to
 * the activity-type label.
 */
export function buildCallTitle(input: TitleInput): string {
  const typeLabel = ACTIVITY_TYPE_LABELS[input.type] || 'פעילות';
  const who = input.contactName || input.companyName || '';
  const base = who ? `${typeLabel} עם ${who}` : typeLabel;
  const resultLabel = input.callResult ? CALL_RESULT_LABELS[input.callResult] : '';

  if (input.type === 'call') {
    const connectionLabel = input.callConnection
      ? CALL_CONNECTION_LABELS[input.callConnection]
      : '';
    const outcome = [connectionLabel, resultLabel].filter(Boolean).join(' / ');
    return outcome ? `${base} · ${outcome}` : base;
  }

  // meeting / email / note — "<type> עם <name>[ · <result>]"
  return resultLabel ? `${base} · ${resultLabel}` : base;
}

/**
 * Drop keys whose value is `undefined` or `''`. Firestore rejects `undefined`
 * (this repo does not set `ignoreUndefinedProperties`), and we don't want to
 * persist empty-string optionals (e.g. blank entity ids). Callers must ensure
 * required fields are populated before calling.
 */
export function stripBlank<T extends Record<string, unknown>>(obj: T): Partial<T> {
  const out: Partial<T> = {};
  for (const [key, value] of Object.entries(obj)) {
    if (value === undefined || value === '') continue;
    out[key as keyof T] = value as T[keyof T];
  }
  return out;
}

/**
 * Inline follow-up payload captured by FollowUpField. `assigneeId`/`assigneeName`
 * default the resulting ticket to the logged-in user but can be reassigned by the user;
 * when omitted, the ticket falls back to the activity's performer.
 */
export interface FollowUpData {
  date: string;
  notes?: string;
  assigneeId?: string;
  assigneeName?: string;
}

/**
 * Build the follow-up task (a tasks-app ticket) that accompanies an activity when the
 * user schedules one inline. The assignee defaults to the chosen follow-up assignee (the
 * logged-in user by default), falling back to the activity's performer.
 */
export function buildFollowUpTask(activity: ActivityInput, followUp: FollowUpData): NewTicketInput {
  const description =
    followUp.notes || (activity.description ? `בהמשך לפעילות: ${activity.description}` : undefined);

  return {
    title: `מעקב: ${activity.title}`,
    description,
    dueDate: followUp.date || getTomorrowISO(),
    priority: 'normal',
    assigneeId: followUp.assigneeId || activity.performedById,
    // Never undefined next to an assigneeId: Firestore rejects undefined fields.
    assigneeName: followUp.assigneeName || activity.performedByName || '',
    dealId: activity.dealId,
    dealName: activity.dealName,
    customer: activity.companyName,
  };
}
